import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { getChemistName } from "@/utils/textUtils";

const NAVY = "#002169";

function fmtDate(iso) {
  if (!iso) return "";
  const p = String(iso).split("T")[0].split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0].slice(2)}` : iso;
}

/**
 * Manage the pharmacies hidden from Check-Ins.
 *
 * These are internal/test accounts (Glenn's "Ventura Pharmacy" on production being
 * the motivating case) that would otherwise sit in the At-Risk list forever and skew
 * the subscription counts. Hiding is reversible and recorded against whoever did it.
 *
 * @param {{chemistUUID: string, chemistName: string}} [presetChemist]
 *   Pre-fills the add form — set when opened from a grid row's "hide" action.
 */
const CheckInExclusionsModal = ({ open, onClose, presetChemist = null, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Add form
  const [chemistOptions, setChemistOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const [removingUUID, setRemovingUUID] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await phappsRequest(API_PATHS.CHECK_INS_EXCLUSIONS, "GET");
      setRows(res?.exclusions || []);
      setError(null);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load the ignored list"));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadChemistOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const res = await phappsRequest(API_PATHS.CHEMIST_LIST, "GET");
      setChemistOptions(
        (res?.chemists ?? [])
          .map((c) => ({
            chemistUUID: c.PHUUID ?? c.ChemistUUID ?? "",
            chemistName: c.chemistName ?? c.ChemistName ?? "",
          }))
          .filter((o) => o.chemistUUID && o.chemistName)
      );
    } catch {
      setChemistOptions([]); // the preset path still works without the full list
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setReason("");
    setSelected(presetChemist || null);
    load();
    loadChemistOptions();
  }, [open, presetChemist, load, loadChemistOptions]);

  const alreadyHidden = useMemo(
    () => new Set(rows.map((r) => r.chemistUUID)),
    [rows]
  );

  const add = async () => {
    if (!selected?.chemistUUID) return;
    setSaving(true);
    setError(null);
    try {
      await phappsRequest(API_PATHS.CHECK_INS_EXCLUSIONS, "POST", {
        chemistUUID: selected.chemistUUID,
        reason: reason.trim() || null,
      });
      setSelected(null);
      setReason("");
      await load();
      onChanged?.();
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to hide that pharmacy"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (chemistUUID) => {
    setRemovingUUID(chemistUUID);
    setError(null);
    try {
      await phappsRequest(API_PATHS.CHECK_INS_EXCLUSION(chemistUUID), "DELETE");
      await load();
      onChanged?.();
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to restore that pharmacy"));
    } finally {
      setRemovingUUID(null);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2.5,
          py: 1.75,
          backgroundColor: NAVY,
          color: "#fff",
        }}
      >
        <VisibilityOffOutlinedIcon />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", lineHeight: 1.25 }}>
            Ignored pharmacies
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", opacity: 0.85 }}>
            Hidden from every Check-Ins table and count
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} sx={{ color: "#fff" }} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 2.5 }}>
        <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mb: 1.5 }}>
          Use this for test and internal accounts — anything that isn't a real customer and
          would otherwise sit in At-Risk forever. Restoring one puts it straight back.
        </Typography>

        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
          <Autocomplete
            size="small"
            sx={{ flex: "1 1 260px", minWidth: 220 }}
            options={chemistOptions}
            loading={optionsLoading}
            value={selected}
            onChange={(_, v) => setSelected(v)}
            getOptionLabel={(o) => getChemistName(o?.chemistName || "")}
            isOptionEqualToValue={(o, v) => o.chemistUUID === v?.chemistUUID}
            getOptionDisabled={(o) => alreadyHidden.has(o.chemistUUID)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pharmacy"
                placeholder="Search…"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {optionsLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            size="small"
            label="Reason (optional)"
            placeholder="e.g. Glenn's test account"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ flex: "1 1 220px", minWidth: 180 }}
            inputProps={{ maxLength: 255 }}
          />
          <Button
            variant="contained"
            onClick={add}
            disabled={saving || !selected?.chemistUUID}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              backgroundColor: NAVY,
              height: 40,
              "&:hover": { backgroundColor: "#001a54" },
            }}
          >
            {saving ? "Hiding…" : "Hide"}
          </Button>
        </Box>

        {error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}

        <Divider sx={{ my: 2 }} />

        <Typography
          sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4, mb: 1 }}
        >
          Currently ignored ({rows.length})
        </Typography>

        {loading ? (
          <Typography variant="caption" sx={{ color: "#9ca3af" }}>Loading…</Typography>
        ) : rows.length === 0 ? (
          <Typography variant="caption" sx={{ color: "#9ca3af" }}>
            Nothing is ignored — every subscribed pharmacy appears in Check-Ins.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, maxHeight: 300, overflowY: "auto", pr: 0.5 }}>
            {rows.map((r) => (
              <Box
                key={r.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  px: 1.5,
                  py: 1,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#374151" }} noWrap>
                    {getChemistName(r.chemistName)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#9ca3af" }} noWrap>
                    {r.reason || "No reason recorded"}
                    {r.excludedByName ? ` · ${r.excludedByName}` : ""}
                    {r.excludedAt ? ` · ${fmtDate(r.excludedAt)}` : ""}
                  </Typography>
                </Box>
                <Tooltip arrow title="Restore — show this pharmacy in Check-Ins again">
                  <span>
                    <Button
                      size="small"
                      onClick={() => remove(r.chemistUUID)}
                      disabled={removingUUID === r.chemistUUID}
                      startIcon={<UndoOutlinedIcon sx={{ fontSize: 15 }} />}
                      sx={{ textTransform: "none", fontWeight: 700, color: NAVY, flexShrink: 0 }}
                    >
                      {removingUUID === r.chemistUUID ? "…" : "Restore"}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CheckInExclusionsModal;
